import { useCallback, useState } from 'react';
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { endTreatment } from '../../../../services/patientFiles';
import type { ActionRunnerProps } from '../types';

// Retainer ordering used to live inside this action. It has been split out —
// retainers will be handled in their own dedicated section. End-treatment now
// just closes the case (Complete / Done) without touching retainer orders.

type Step = 'confirm' | 'success' | 'error';

function EndTreatmentAction({ patientId, patientName, onBack, onDataChange }: ActionRunnerProps) {
    const [step, setStep] = useState<Step>('confirm');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleSubmit = useCallback(async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        setErrorMessage(null);
        try {
            await endTreatment(patientId);
            onDataChange?.();
            setStep('success');
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to end treatment';
            setErrorMessage(message);
            setStep('error');
        } finally {
            setIsSubmitting(false);
        }
    }, [isSubmitting, patientId, onDataChange]);

    const handleSuccessBack = useCallback(() => {
        onBack();
    }, [onBack]);

    const handleRetry = useCallback(() => {
        setStep('confirm');
        setErrorMessage(null);
    }, []);

    if (step === 'success') {
        return (
            <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                    <div className="w-[120px]" />
                    <div className="text-sm font-semibold text-gray-800">End treatment</div>
                    <div className="w-[120px]" />
                </div>
                <div className="flex-1 min-h-0 overflow-auto p-6">
                    <div className="max-w-xl mx-auto">
                        <div className="flex items-start gap-3 p-4 rounded-xl border border-green-200 bg-green-50">
                            <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-green-800">
                                <div className="font-semibold mb-1">
                                    Treatment ended successfully.
                                </div>
                                <p>
                                    <span className="font-medium">{patientName}</span>&apos;s case
                                    has been closed.
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={handleSuccessBack}
                                className="bg-gray-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-800 transition-colors"
                            >
                                Back to actions
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'error') {
        return (
            <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                    >
                        <ArrowLeftIcon className="w-3.5 h-3.5" />
                        Back to actions
                    </button>
                    <div className="text-sm font-semibold text-gray-800">End treatment</div>
                    <div className="w-[120px]" />
                </div>
                <div className="flex-1 min-h-0 overflow-auto p-6">
                    <div className="max-w-xl mx-auto">
                        <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50">
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-red-800">
                                <div className="font-semibold mb-1">Something went wrong.</div>
                                <p>{errorMessage}</p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={onBack}
                                className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRetry}
                                className="bg-gray-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-800 transition-colors"
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={onBack}
                    className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                >
                    <ArrowLeftIcon className="w-3.5 h-3.5" />
                    Back to actions
                </button>
                <div className="text-sm font-semibold text-gray-800">End treatment</div>
                <div className="w-[120px]" />
            </div>

            <div className="flex-1 min-h-0 overflow-auto p-6">
                <div className="max-w-xl mx-auto">
                    <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            Patient
                        </div>
                        <div className="text-base font-semibold text-gray-900">{patientName}</div>
                    </div>

                    <div className="mt-6 text-sm text-gray-600 space-y-2">
                        <p>Are you sure you want to end this patient&apos;s treatment?</p>
                        <p>This will close the case. Retainer ordering is handled separately.</p>
                    </div>

                    <div className="mt-6 flex justify-end items-center gap-3">
                        <button
                            onClick={onBack}
                            disabled={isSubmitting}
                            className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="flex items-center gap-2 bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting && (
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            )}
                            {isSubmitting ? 'Ending…' : 'Yes, end treatment'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { EndTreatmentAction };
